import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import html2canvas from "html2canvas";

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));

const waitForImages = (element) => Promise.all(
  Array.from(element.querySelectorAll("img")).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      const finish = () => {
        clearTimeout(timeout);
        img.removeEventListener("load", finish);
        img.removeEventListener("error", finish);
        resolve();
      };
      const timeout = setTimeout(finish, 10000);
      img.addEventListener("load", finish);
      img.addEventListener("error", finish);
    });
  })
);

// Keep a single screenshot card in the DOM and at most four requests in flight.
export default function useBulkPrintPreparation(loadRecord) {
  const [items, setItems] = useState([]);
  const [captureRecord, setCaptureRecord] = useState(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [progress, setProgress] = useState("");
  const captureRef = useRef(null);
  const runId = useRef(0);
  const screenshotUrls = useRef([]);

  const releaseScreenshots = useCallback(() => {
    screenshotUrls.current.forEach((url) => URL.revokeObjectURL(url));
    screenshotUrls.current = [];
  }, []);

  useEffect(() => () => {
    runId.current += 1;
    screenshotUrls.current.forEach((url) => URL.revokeObjectURL(url));
    screenshotUrls.current = [];
  }, []);

  const reset = useCallback(() => {
    runId.current += 1;
    releaseScreenshots();
    setItems([]);
    setCaptureRecord(null);
    setIsPreparing(false);
    setProgress("");
  }, [releaseScreenshots]);

  const prepare = async (ids) => {
    if (!ids.length) return false;
    const currentRun = ++runId.current;
    releaseScreenshots();
    setItems([]);
    setIsPreparing(true);
    const results = [];
    const isCurrent = () => currentRun === runId.current;

    try {
      for (let offset = 0; offset < ids.length; offset += 4) {
        if (!isCurrent()) return false;
        setProgress(`Chargement des documents (${offset + 1}/${ids.length})…`);
        // Wait for the entire batch, including failures, before starting another.
        const batch = await Promise.allSettled(
          ids.slice(offset, offset + 4).map((id) => loadRecord(id, true))
        );
        if (!isCurrent()) return false;
        const failure = batch.find((result) => result.status === "rejected");
        if (failure) throw failure.reason;

        for (const result of batch) {
          if (!isCurrent()) return false;
          const data = result.value;
          flushSync(() => {
            setCaptureRecord(data);
            setProgress(`Préparation du document (${results.length + 1}/${ids.length})…`);
          });
          await yieldToBrowser();
          const element = captureRef.current;
          if (!element) throw new Error("Screenshot card unavailable");
          await waitForImages(element);
          if (!isCurrent()) return false;

          const canvas = await html2canvas(element, {
            useCORS: true,
            allowTaint: false,
            imageTimeout: 10000,
            scale: 0.8,
            width: element.scrollWidth,
            height: element.scrollHeight,
          });
          try {
            if (!isCurrent()) return false;
            const blob = await new Promise((resolve, reject) => {
              canvas.toBlob((value) => value ? resolve(value) : reject(new Error("Screenshot encoding failed")), "image/jpeg", 0.85);
            });
            if (!isCurrent()) return false;
            const screenshot = URL.createObjectURL(blob);
            screenshotUrls.current.push(screenshot);
            results.push({ data, screenshot });
          } finally {
            canvas.width = 0;
            canvas.height = 0;
          }
          await yieldToBrowser();
        }
      }
      if (!isCurrent()) return false;
      setItems(results);
      return true;
    } catch (error) {
      if (!isCurrent()) return false;
      releaseScreenshots();
      throw error;
    } finally {
      if (isCurrent()) {
        setCaptureRecord(null);
        setIsPreparing(false);
        setProgress("");
      }
    }
  };

  return { items, captureRecord, captureRef, isPreparing, progress, prepare, reset };
}
